/**
 * The current live Gemini pin. Every field here is also consumed by
 * `providerConfig.ts`, so the active model, request limits, and generation
 * config version cannot drift apart.
 *
 * **Pricing** — https://ai.google.dev/gemini-api/docs/pricing (checked
 * 2026-08-17): Standard paid-tier pricing is $0.75 / 1M input tokens and
 * $3.75 / 1M output tokens, including thinking tokens, through 2026-12-31.
 * The same page publishes the post-promotion $1.50 / $7.50 rates beginning
 * 2027-01-01; the registry records the rate that is live on the checked date.
 * Context caching is $0.075 / 1M input tokens through 2026-12-31.
 *
 * **Limits/capabilities** — https://ai.google.dev/gemini-api/docs/models/gemini-3.7-flash
 * (checked 2026-08-17) publishes a 1,048,576-token input limit, a 65,536-token
 * maximum output, and support for structured outputs, function calling,
 * PDF/multimodal input, and thinking.
 * https://ai.google.dev/gemini-api/docs/image-understanding publishes the
 * 20 MB total inline request ceiling used by `totalRequestBytesLimit`.
 */
const GEMINI_APPROVED_ENTRY = {
    provider: "gemini",
    modelId: "gemini-3.7-flash",
    displayName: "Gemini 3.7 Flash",
    totalRequestBytesLimit: 20_000_000,
    inputTokenLimit: 1_048_576,
    inputUsdPerMillion: 0.75,
    outputUsdPerMillion: 3.75,
    cachedInputUsdPerMillion: 0.075,
    generationConfigVersion: "gemini-pinned-sampling-2026-08-17-v1",
    verifiedAt: "2026-08-17"
};
/**
 * This retained entry exists to price historical rows at the rate they were
 * actually billed; do not update it to current promotional pricing.
 */
const GEMINI_PREVIOUS_APPROVED_ENTRY = {
    provider: "gemini",
    modelId: "gemini-3.6-flash",
    displayName: "Gemini 3.6 Flash (previous production pin)",
    totalRequestBytesLimit: 20_000_000,
    inputTokenLimit: 1_048_576,
    inputUsdPerMillion: 1.5,
    outputUsdPerMillion: 7.5,
    generationConfigVersion: "gemini-pinned-sampling-2026-07-25-v1",
    verifiedAt: "2026-07-31"
};
/**
 * The one approved OpenAI pair. Model id and pricing verified 2026-08-17
 * against OpenAI's official docs. Note that `platform.openai.com/docs/*` now
 * 301-redirects to `developers.openai.com/api/docs/*`; the canonical host is
 * cited below.
 *
 * **Pricing** — https://developers.openai.com/api/docs/pricing and
 * https://developers.openai.com/api/docs/models/gpt-5.6-sol: `gpt-5.6-sol`
 * short-context $5.00 / 1M input, $0.50 / 1M cached input, $30.00 / 1M output.
 * The model page also states
 * verbatim: "Prompts with >272K input tokens are priced at 2x input and 1.5x
 * output for the full request", i.e. $10.00 / $1.00 cached input / $45.00
 * long-context. The registry records both published tiers.
 *
 * **`inputTokenLimit: 922_000`** (S1 verification follow-up #3/#5, resolved
 * here). https://developers.openai.com/api/docs/models/gpt-5.6-sol documents a
 * *context window* of 1,050,000 tokens and *max output* of 128,000. OpenAI
 * publishes no separate "max input tokens" field for this family — the
 * frequently-quoted 272,000 figure is the **pricing** threshold above, not an
 * input cap, and applying it here would have under-admitted by ~3.5x. The
 * registry field means "largest input this model accepts", so it is derived
 * as context window - max output = 1,050,000 - 128,000 = 922,000: the largest
 * input that can still coexist with the model's full documented output
 * budget. Consumers subtract their own configured output budget and safety
 * headroom on top, so admission stays conservative.
 *
 * **`totalRequestBytesLimit: 50_000_000`** (S1 verification follow-up #3).
 * OpenAI documents **no** general HTTP request-body byte cap for
 * `POST /v1/responses` — the rate-limit docs are expressed only in RPM/TPM/RPD
 * terms and the binding constraint on a text request is `inputTokenLimit`
 * above (with `truncation: "disabled"` the request 400s past the context
 * window). The tightest *published* per-request payload figure that bounds a
 * Responses request is the file-input ceiling:
 * https://developers.openai.com/api/docs/guides/file-inputs — "each file must
 * be under 50 MB. The combined limit across all files in the request is
 * 50 MB". (The image-input ceiling is looser still at 512 MB total payload:
 * https://developers.openai.com/api/docs/guides/images-vision.) 50,000,000 is
 * therefore a real, cited OpenAI number rather than the previous Gemini copy;
 * it is a backstop, not the operative guard.
 *
 * **`reasoningEffort`** — the ladder mandated by
 * `_build/LLM-PROVIDER-PLAN-2026-07-31.md` §4 (thinkingBudget 0 -> minimal,
 * 1-512 -> low, 513-2048 -> medium, >2048 -> high). All four names are valid
 * Responses API values per
 * https://developers.openai.com/api/docs/guides/reasoning. Caveat recorded
 * honestly: the gpt-5.6 model page does not publish which subset *this* model
 * supports (the gpt-5 page publishes "minimal, low, medium, and high"), which
 * is exactly why the ladder is registry-declared and per-model overridable.
 */
const OPENAI_APPROVED_ENTRY = {
    provider: "openai",
    modelId: "gpt-5.6-sol",
    displayName: "GPT-5.6 Sol",
    totalRequestBytesLimit: 50_000_000,
    inputTokenLimit: 922_000,
    inputUsdPerMillion: 5,
    outputUsdPerMillion: 30,
    cachedInputUsdPerMillion: 0.5,
    longContextThresholdTokens: 272_000,
    longContextInputUsdPerMillion: 10,
    longContextCachedInputUsdPerMillion: 1,
    longContextOutputUsdPerMillion: 45,
    generationConfigVersion: "openai-pinned-sampling-2026-07-31-v1",
    reasoningEffort: {
        bands: [
            { maxThinkingBudget: 0, effort: "minimal" },
            { maxThinkingBudget: 512, effort: "low" },
            { maxThinkingBudget: 2_048, effort: "medium" }
        ],
        aboveHighestBand: "high"
    },
    verifiedAt: "2026-08-17"
};
/**
 * The cost-efficient OpenAI tier, added under the stage-S4 registry addendum.
 *
 * **Why it is here at all, and what it is not.** Rich asked for "Codex Luna
 * Max". Verified 2026-07-31 against OpenAI's own docs: *no API model carries
 * that id*. The string is three separate product concepts run together —
 *  - **Codex** is a product surface. In the GPT-5.6 generation it has no model
 *    of its own: OpenAI's launch material states "Codex offers three GPT-5.6
 *    models: Sol for detail and polish, Terra as the everyday workhorse, and
 *    Luna for clear, repeatable work" (https://openai.com/index/gpt-5-6/), i.e.
 *    Codex runs the same three tiers the API exposes. The only *current*
 *    Codex-branded API model is `gpt-5.3-codex`
 *    (https://developers.openai.com/api/docs/models/gpt-5.3-codex), a previous
 *    generation, and it is deliberately NOT registered — see the S4 addendum in
 *    `_build/LLM-PROVIDER-PLAN-2026-07-31.md`.
 *  - **Luna** is the GPT-5.6 cost tier, and *is* a real API model: this entry.
 *  - **Max** is a reasoning-effort setting, not a model. It is toggled in
 *    ChatGPT Work and Codex, and appears in the API as `reasoning.effort: "max"`
 *    (https://developers.openai.com/api/docs/guides/reasoning).
 *
 * So the API equivalent of "Codex Luna Max" is *this model plus a reasoning
 * effort*, which is exactly how the registry already models it: the id below
 * and the `reasoningEffort` ladder are separate fields.
 *
 * **Verified 2026-08-17 from https://developers.openai.com/api/docs/models/gpt-5.6-luna:**
 * endpoints — "Responses (v1/responses) - Supported"; features — "streaming,
 * structured_outputs, function_calling, file_search, image_input, web_search,
 * prompt_caching", so `text.format` json_schema strict (the adapter's only
 * output mode) is supported; pricing — $0.20 / 1M input, $0.02 / 1M cached
 * input, $1.20 / 1M output; above 272K input tokens the long-context rates are
 * $0.40 / $0.04 / $1.80; "1,050,000 context window" and "128,000 max output
 * tokens"; one snapshot, `gpt-5.6-luna`, so the id is not an alias. That is
 * 25x cheaper input and output than `gpt-5.6-sol` above, which is the
 * cost/performance point Rich was after.
 *
 * `inputTokenLimit` and `totalRequestBytesLimit` are derived exactly as for the
 * Sol entry above (context window - max output; the published 50 MB combined
 * file-input ceiling), for the same reasons documented there.
 *
 * **`reasoningEffort`** — the reviewed Luna policy preserves the lower
 * minimal/low/medium bands, maps larger explicit budgets to `max`, and declares
 * `max` as the default when a caller supplies no thinking budget. This keeps
 * the model's standing app setting in the registry rather than in adapter code;
 * a caller that explicitly supplies a smaller budget still selects its band.
 */
const OPENAI_LUNA_APPROVED_ENTRY = {
    provider: "openai",
    modelId: "gpt-5.6-luna",
    displayName: "GPT-5.6 Luna",
    totalRequestBytesLimit: 50_000_000,
    inputTokenLimit: 922_000,
    inputUsdPerMillion: 0.2,
    outputUsdPerMillion: 1.2,
    cachedInputUsdPerMillion: 0.02,
    longContextThresholdTokens: 272_000,
    longContextInputUsdPerMillion: 0.4,
    longContextCachedInputUsdPerMillion: 0.04,
    longContextOutputUsdPerMillion: 1.8,
    generationConfigVersion: "openai-pinned-sampling-2026-07-31-v1",
    reasoningEffort: {
        bands: [
            { maxThinkingBudget: 0, effort: "minimal" },
            { maxThinkingBudget: 512, effort: "low" },
            { maxThinkingBudget: 2_048, effort: "medium" }
        ],
        aboveHighestBand: "max",
        defaultEffort: "max"
    },
    verifiedAt: "2026-08-17"
};
export const APPROVED_LLM_MODELS = [
    GEMINI_APPROVED_ENTRY,
    GEMINI_PREVIOUS_APPROVED_ENTRY,
    OPENAI_APPROVED_ENTRY,
    OPENAI_LUNA_APPROVED_ENTRY
];
/** The registry default pair: the live Gemini entry. */
export const DEFAULT_APPROVED_LLM_PAIR = GEMINI_APPROVED_ENTRY;
/** The current approved Gemini model id for derived consumers. */
export const GEMINI_APPROVED_MODEL_ID = GEMINI_APPROVED_ENTRY.modelId;
/** The previous production Gemini model id retained for compatibility. */
export const GEMINI_PREVIOUS_APPROVED_MODEL_ID = GEMINI_PREVIOUS_APPROVED_ENTRY.modelId;
/** The oldest Gemini id retained only for persisted-artifact compatibility. */
export const GEMINI_LEGACY_PERSISTED_MODEL_ID = "gemini-3.5-flash";
/**
 * The pair `LLM_PROVIDER=openai` pins to until stage S9 makes the model
 * selectable from settings. Deliberately the flagship rather than the cheapest
 * entry: S4 is a like-for-like parity pilot against the Gemini pin, so the
 * comparison must not be confounded by also dropping a tier. Selecting
 * {@link OPENAI_LUNA_APPROVED_ENTRY} becomes a settings change, not a code
 * change, once S9 lands.
 */
export const DEFAULT_OPENAI_APPROVED_PAIR = OPENAI_APPROVED_ENTRY;
/**
 * The approved OpenAI model id, exported so callers outside this file (e.g.
 * `apps/server/src/costPricing.ts`'s pricing table) never need to spell the
 * literal id out themselves — `activeModelLivePins.test.ts` enforces that
 * this file is the sole source of every approved provider model id.
 */
export const OPENAI_APPROVED_MODEL_ID = OPENAI_APPROVED_ENTRY.modelId;
/**
 * The approved OpenAI Luna model id, exported for the same reason as
 * {@link OPENAI_APPROVED_MODEL_ID} — `activeModelLivePins.test.ts` enforces
 * that this file is the sole source of every approved provider model id.
 */
export const OPENAI_LUNA_APPROVED_MODEL_ID = OPENAI_LUNA_APPROVED_ENTRY.modelId;
const findApprovedLlmModelEntry = (provider, modelId) => APPROVED_LLM_MODELS.find((entry) => entry.provider === provider && entry.modelId === modelId);
export function isApprovedLlmPair(provider, modelId) {
    return findApprovedLlmModelEntry(provider, modelId) !== undefined;
}
/**
 * The full registry entry for an approved pair, or `undefined` when the pair
 * is not approved. Provider adapters use this to read per-model capability
 * (`inputTokenLimit`, `totalRequestBytesLimit`, `reasoningEffort`) instead of
 * carrying their own copies.
 */
export function getApprovedLlmModelEntry(provider, modelId) {
    return findApprovedLlmModelEntry(provider, modelId);
}
export function getApprovedLlmPairs() {
    return APPROVED_LLM_MODELS;
}
/**
 * Resolve a caller-supplied (provider, modelId) request against the
 * registry, generalizing `clampGeminiActiveModel`'s single-model clamp to any
 * approved pair. No modelId requested is treated as "no override" (not
 * clamped). A modelId is checked against the requested provider, falling
 * back to the registry default provider when none is given — this keeps
 * Gemini-only callers (which only ever pass a model string) working exactly
 * as before. Anything not found in the registry falls back to the default
 * pair, loudly recording what was requested rather than silently resolving.
 */
export function resolveApprovedLlmPair(requested) {
    const requestedModelId = requested?.modelId?.trim() || undefined;
    if (!requestedModelId) {
        return {
            provider: DEFAULT_APPROVED_LLM_PAIR.provider,
            modelId: DEFAULT_APPROVED_LLM_PAIR.modelId,
            clamped: false
        };
    }
    const requestedProvider = requested?.provider?.trim() || undefined;
    const effectiveProvider = requestedProvider ?? DEFAULT_APPROVED_LLM_PAIR.provider;
    const matched = findApprovedLlmModelEntry(effectiveProvider, requestedModelId);
    if (matched) {
        return {
            provider: matched.provider,
            modelId: matched.modelId,
            clamped: false
        };
    }
    return {
        provider: DEFAULT_APPROVED_LLM_PAIR.provider,
        modelId: DEFAULT_APPROVED_LLM_PAIR.modelId,
        ...(requestedProvider ? { requestedProvider } : {}),
        requestedModelId,
        clamped: true
    };
}
//# sourceMappingURL=llmModelRegistry.js.map